import React from "react";
import { ReactSVG } from "react-svg";

// region Libraries
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import ScheduleIcon from "@material-ui/icons/Schedule";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, TravelDataMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Interfaces
import { Travel } from "@shared/interfaces/travel.interface";
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { Location } from "@shared/interfaces/location.interface";
import { TLanguages } from "@shared/languages/languageData";
// endregion Interfaces
// region Utils
import utils from "../../utils/useful-functions";
// endregion Utils
// region Assets
import IconObra from "../../assets/stateIcons/Na obra.svg";
// endregion Assets
// region Styles
import { Container, ContainerTravelDataListItem } from "./styles";
// endregion Styles

type TravelDataListItemProps = { title: string, content: string }
const TravelDataListItem = React.memo<TravelDataListItemProps>((
  { content, title }
) => (
  <ContainerTravelDataListItem className="item">
    <div className="title">{title}</div>
    <div className="content">{content}</div>
  </ContainerTravelDataListItem>
));

/** One-line address of a location: "Street, 12 - District - Complement · City - UF" (empty parts are left out)
 * @param location Origin, destination or final location of the travel
 */
const formatAddress = (location: Location): string => {
  const street = [location.address, location.number].filter((part) => part !== undefined && part !== null && `${part}` !== "").join(", ");
  const local = [street, location.district, location.complement].filter(Boolean).join(" - ");
  const city = [location.county, location.uf].filter(Boolean).join(" - ");

  return [local, city].filter(Boolean).join(" · ");
};

type TravelStopProps = { label: string, name: string, address: string, icon: React.ReactNode, tag?: string, last?: boolean };

/** A stop of the itinerary (origin, destination, final location): its icon on the line, then what and where */
const TravelStop: React.FC<TravelStopProps> = ({ label, name, address, icon, tag, last = false }) => (
  <li className={`stop${last ? " is-last" : ""}`}>
    <div className="stop-icon" aria-hidden="true">{icon}</div>
    <div className="stop-text">
      <div className="stop-label">
        {label}
        {tag && <span className="stop-tag">{tag}</span>}
      </div>
      <div className="stop-name">{name}</div>
      {address && <div className="stop-address">{address}</div>}
    </div>
  </li>
);

type TravelInfoRow = { label: string, value: string };
type TravelInfoCardProps = {
  icon: React.ReactNode;
  tone: "note" | "load" | "travel";
  title: string;
  /** The value that names the group (the note number, the load volume), shown big in the header */
  headline?: string;
  rows: TravelInfoRow[];
};

/** A group of related travel data: icon, title and headline value, then its rows (and anything else it holds) */
const TravelInfoCard: React.FC<TravelInfoCardProps> = ({ icon, tone, title, headline, rows, children }) => (
  <section className={`info-card is-${tone}`}>
    <header className="info-card__header">
      <span className="info-card__icon" aria-hidden="true">{icon}</span>
      <span className="info-card__title">{title}</span>
      {headline && <span className="info-card__headline">{headline}</span>}
    </header>
    {rows.length > 0 && (
      <dl className="info-card__rows">
        {rows.map((row) => (
          <div className="info-card__row" key={`info-row-${row.label}`}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    )}
    {children}
  </section>
);

type TravelDataProps = { travel: Travel, vehicle: Vehicle };
const TravelData: React.FC<TravelDataProps> = ({ travel, vehicle }) => {
  const { t, i18n } = useTranslation();

  const stops: TravelStopProps[] = [
    {
      label: t(TravelDataMessages.origin),
      name: travel.origin.name || "",
      address: formatAddress(travel.origin),
      icon: vehicle.states.length > 0 && utils.getStateIcon(vehicle.states[0].status.id_vehicle_state_type)
    }
  ];

  if (travel.destination) {
    stops.push({
      label: t(TravelDataMessages.destination),
      tag: travel.client || t(GlobalMessages.noClientRegistered),
      name: travel.destination.name || t(GlobalMessages.noNameRegistered),
      address: formatAddress(travel.destination),
      icon: <ReactSVG src={IconObra} />
    });
  }
  if (travel.end) {
    stops.push({
      label: t(TravelDataMessages.finalLocation),
      name: travel.end.name || "",
      address: formatAddress(travel.end),
      icon: utils.getStateIcon(vehicle.states[vehicle.states.length - 1]?.status.id_vehicle_state_type)
    });
  }

  // Dates without seconds: the minute is what matters on screen
  const date = (value?: Date | string) => (value ? `${utils.formatDateIfHave(value, "fullDate")}`.slice(0, -3) : "");
  const rows = (list: (TravelInfoRow | false | undefined | "")[]) => list.filter((row): row is TravelInfoRow => !!row && !!row.value);

  // The delivery note with its own dates; a travel without note shows when it started
  const hasNote = !!(travel?.num_doc || travel?.document_date || travel?.integration_date);
  const noteRows = rows([
    { label: t(TravelDataMessages.invoiceIssueDate), value: date(travel?.document_date) },
    { label: t(TravelDataMessages.invoiceIntegrationDate), value: date(travel?.integration_date) },
    !travel?.integration_date && { label: t(TravelDataMessages.travelStartDate), value: date(travel?.registration_date) }
  ]);

  // The load: seal, expiration and the piece it goes to (a volume of 0 is not shown)
  const volume = travel?.volume ? `${utils.formatNumberToLocaleString(Number(travel.volume), i18n.language as TLanguages)} m³` : "";
  const loadRows = rows([
    { label: t(TravelDataMessages.sealNumber), value: travel?.load_label ? `${travel.load_label}` : "" },
    { label: t(TravelDataMessages.loadExpirationDate), value: date(travel?.concrete_expiration) },
    { label: t(TravelDataMessages.pieceToBeConcreted), value: travel?.concrete_order ?? "" }
  ]);
  const hasLoad = !!(volume || loadRows.length || travel?.concrete_trace);

  return (
    <Container>
      <ol className="itinerary">
        {stops.map((stop, index) => (
          <TravelStop key={`travel-stop-${stop.label}`} {...stop} last={index === stops.length - 1} />
        ))}
      </ol>
      <div className="info-cards">
        {hasNote ? (
          <TravelInfoCard
            icon={<ReceiptOutlinedIcon />}
            tone="note"
            title={t(TravelDataMessages.taxInvoice)}
            headline={travel?.num_doc ? `${travel.num_doc}` : undefined}
            rows={noteRows}
          />
        ) : (
          <TravelInfoCard
            icon={<ScheduleIcon />}
            tone="travel"
            title={t(TravelDataMessages.travelStartDate)}
            headline={date(travel?.registration_date)}
            rows={[]}
          />
        )}
        {hasLoad && (
          <TravelInfoCard
            icon={<LocalShippingOutlinedIcon />}
            tone="load"
            title={t(TravelDataMessages.load)}
            headline={volume || undefined}
            rows={loadRows}
          >
            { travel?.concrete_trace && (
              <Accordion className="trace">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography component="div">{t(TravelDataMessages.concreteTrace)}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography component="div">
                    {/* eslint-disable */ }
                    { travel?.concrete_trace?.application && <TravelDataListItem title={t(TravelDataMessages.application)} content={ travel.concrete_trace.application }/> }
                    { travel?.concrete_trace?.slump && <TravelDataListItem title={t(TravelDataMessages.slump)} content={ travel.concrete_trace.slump.toString() }/> }
                    { travel?.concrete_trace?.resistance && <TravelDataListItem title={t(TravelDataMessages.resistance)} content={ travel.concrete_trace.resistance }/> }
                    { travel?.concrete_trace?.resistance_type && <TravelDataListItem title={t(TravelDataMessages.resistanceType)} content={ travel.concrete_trace.resistance_type.abbreviation }/> }
                    { travel?.concrete_trace?.application && <TravelDataListItem title={t(TravelDataMessages.resistanceComplement)} content={ travel.concrete_trace.resistance_complement }/> }
                    { travel?.concrete_trace?.gravel && <TravelDataListItem title={t(TravelDataMessages.gravel)} content={ travel.concrete_trace.gravel.abbreviation }/> }
                    {/* eslint-enable */ }
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ) }
          </TravelInfoCard>
        )}
      </div>
    </Container>
  );
};

export default TravelData;
